import { AnimatePresence, motion } from 'framer-motion';
import { isEmpty } from 'lodash';
import styled from 'styled-components';
import type { FinderItem } from '@frameio/graphql-client';
import type {
  TransferBatchStatusEnum,
  TransferJobType,
} from '@frameio/graphql-client/generated';
import { Thumbnails } from '@frameio/shared/components/Thumbnails';
import { getThumbMedia } from '@frameio/shared/finder/utils';
import type { MediaDataType } from '@frameio/shared/transfers/types';
import type { DeepPartial } from '@frameio/utils';
import { getIconByFileType } from '@frameio/utils';
import { SINGLE_THUMB } from '@frameio/utils/thumbnails';
import { theme } from '@frameio/vapor';
import type { TransferBatch } from '../../types/uploader';

const CROSSFADE_TRANSITION_DURATION = 0.3;
// If a job includes more than n folders, don't show thumbnails
const FOLDERS_ICON_LIMIT = 2;
const { width, height } = SINGLE_THUMB;

export function JobMediaThumbnail({
  transferBatchAssetType,
  type,
  mimeType,
  job,
  status,
}: {
  transferBatchAssetType?: MediaDataType['__typename'];
  type: TransferJobType;
  mimeType?: string | null;
  job?: TransferBatch;
  status?: TransferBatchStatusEnum;
}) {
  if (!job) {
    return <></>;
  }
  const iconType = (type === 'FILE' ? mimeType : type) ?? 'FILE';
  const { Icon, backgroundColor, color } = getIconByFileType(
    iconType,
    transferBatchAssetType
  );
  const thumbs = job.paginatedAssetItems?.nodes
    .filter((node) => !!node.asset)
    .map((node) => {
      return {
        media: node.asset,
        type:
          (node.asset as DeepPartial<FinderItem>)?.__typename ||
          'UnsupportedAsset',
        id: node.id,
      };
    })
    // Prioritize thumbs with media (as opposed to folders etc. which will show an icon)
    .sort(
      (a, b) =>
        Object.keys(b.media || {}).length - Object.keys(a.media || {}).length
    );
  // Does >1 thumbnail have actual thumbnail *media* to display?
  const hasThumb =
    thumbs &&
    !!thumbs.filter((thumb) => !isEmpty(getThumbMedia(thumb.media || {})))[0];

  const uploadedManyFolders =
    (job.topLevelFolderCount ?? 0) >= FOLDERS_ICON_LIMIT ||
    (job.numOfCreatedFolders ?? 0) >= FOLDERS_ICON_LIMIT;

  const hasSucceeded = status === 'SUCCEEDED';
  const showThumb = hasThumb && !uploadedManyFolders;

  return (
    <Container $height={height} $width={width}>
      <AnimatePresence>
        <CrossFadeContainer
          key={`${job.id}-${showThumb}`}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          initial={!hasSucceeded && { opacity: 0 }}
          transition={{
            duration: CROSSFADE_TRANSITION_DURATION,
            ease: theme.transitions.curves.primary,
          }}
        >
          {showThumb ? (
            <Thumbnails thumbs={thumbs} />
          ) : (
            <IconContainer
              $backgroundColor={backgroundColor}
              $color={color}
              $height={height}
              $width={width}
            >
              <Icon color={color} size={24} />
            </IconContainer>
          )}
        </CrossFadeContainer>
      </AnimatePresence>
    </Container>
  );
}

const CrossFadeContainer = styled(motion.div)`
  bottom: 0;
  left: 0;
  position: absolute;
  right: 0;
  top: 0;
`;

const Container = styled.div<{
  $height: number;
  $width: number;
}>`
  align-items: center;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  height: ${(p) => p.$height}px;
  position: relative;
  width: ${(p) => p.$width}px;

  * {
    margin: auto;
  }
`;

export const IconContainer = styled.div<{
  $height: number;
  $width: number;
  $backgroundColor?: string;
  $color?: string;
}>`
  background-color: ${theme.colors.background.component};
  border-radius: ${theme.radii[4]};
  display: flex;
  flex-direction: column;
  height: ${(p) => p.$height}px;
  width: ${(p) => p.$width}px;
`;
